import type { Field } from '@formily/core';
import { useField, useFieldSchema } from '@formily/react';
import { cloneDeep, isEqual } from 'lodash';
import { observer } from '@formily/reactive-react';
import { Button, Modal } from 'antd';
import type { ButtonProps } from 'antd/es/button';
import styled, { keyframes } from 'styled-components';
import { useState, useMemo, useCallback, useEffect } from 'react';
import type { IFormilySchema, IColumn } from '../../types';
import useNormalizeColumns from '../table/useColumnsNormilizer';
import AddRowForm from './AddRowForm';
import { useToggleVisibility } from '../../../hooks';
import { Table as LibraryTable } from '../../../components/Table/Table';
import { ConfirmModal } from '../../../components';
import { useFormArrayTableActions } from '../../hooks/useFormArrayTableActions';
import { buildTableColumns } from '../../utils/buildTableColumns';
import { applyColumnFilters, filterRows } from '../../utils/applyColumnFilters';
import { useExpandableNestedTables } from '../../hooks/useExpandableNestedTables';
import { useLocalTableView } from '../../hooks/useLocalTableView';
import { usePage } from '../../context/PageContext';
import { buildInitialValuesFromSchemaV2 } from './utils';
import { findRootFormValues } from '../../utils';

export interface IFormArrayTableProps {
  title?: string;
  childSchema?: IFormilySchema;
  modalTitle?: string;
  modelWidth?: string;
  modelContentMaxHeight?: string;
  canEditRow?: boolean;
  canDeleteRow?: boolean;
  canDuplicateRow?: boolean;
  buttonText?: string;
  buttonType?: ButtonProps['type'];
  buttonSize?: ButtonProps['size'];
  buttonBlock?: boolean;
  buttonDanger?: boolean;
  buttonDisabled?: boolean;
  buttonGhost?: boolean;
  buttonVisible?: boolean;
  columns?: IColumn[] | Record<string, IColumn>;
  cancelButtonText?: string;
  saveButtonText?: string;
  hideEmpty?: boolean;
  rowItemName?: string;
}

type FormArrayTableRecord = Record<string, unknown>;

const FormArrayTable = observer(() => {
  const field = useField<Field>();
  const componentProps = (field?.componentProps || {}) as IFormArrayTableProps;

  const pageData = usePage();

  const parentContext = (field.form.props as any).parentContext;
  const context = useMemo(() => {
    return { ...pageData.context, ...parentContext };
  }, [pageData.context, parentContext]);

  const {
    title,
    childSchema,
    modalTitle,
    modelWidth,
    modelContentMaxHeight,
    canEditRow = true,
    canDeleteRow = true,
    canDuplicateRow = false,
    buttonText,
    buttonType = 'primary',
    buttonSize = 'middle',
    buttonBlock = false,
    buttonDanger = false,
    buttonDisabled = false,
    buttonGhost = false,
    buttonVisible = true,
    columns: rawColumns,
    cancelButtonText,
    saveButtonText,
    hideEmpty = false,
    rowItemName = 'row',
  } = componentProps;

  const withChildSchemaForm: IFormilySchema | undefined = useMemo(() => {
    if (
      !childSchema ||
      !childSchema?.properties?.childSchemaForm ||
      typeof childSchema?.properties?.childSchemaForm !== 'string'
    )
      return childSchema;
    return {
      ...childSchema,
      properties: {
        ...childSchema?.properties,
        childSchemaForm: JSON.parse(childSchema?.properties?.childSchemaForm),
      },
    };
  }, [childSchema]);
  /**
   * In case of initilValue exists from BE
   * We need to transform the initialValue to the format expected by the schema
   * So we don't have an error on updating
   */
  useEffect(() => {
    if (
      field.initialValue &&
      Array.isArray(field.initialValue) &&
      field.initialValue.length > 0
    ) {
      const rootData = findRootFormValues(field.form);
      const transformedValues: any = buildInitialValuesFromSchemaV2(
        field.initialValue,
        withChildSchemaForm,
        rootData
      );

      if (!isEqual(transformedValues, field.initialValue)) {
        field.setInitialValue(transformedValues);
      }
    }
  }, [field.initialValue]);

  const schema = useFieldSchema();
  const componentConfig = schema[
    'x-component-props-config'
  ] as IFormArrayTableProps;
  const normalizedColumns = useNormalizeColumns(rawColumns ?? []);

  // Parse any stringified customComponentSchema back to objects
  const propsColumns = useMemo(
    () =>
      normalizedColumns.map(col =>
        typeof col.customComponentSchema === 'string'
          ? {
              ...col,
              customComponentSchema: JSON.parse(col.customComponentSchema),
            }
          : col
      ),
    [normalizedColumns]
  );

  const { disabled } = field;

  const {
    canDeleteRow: canDeleteRowConfig,
    canEditRow: canEditRowConfig,
    canDuplicateRow: canDuplicateRowConfig,
  } = componentConfig || {};

  const { isVisible, show, hide } = useToggleVisibility();
  const {
    isVisible: isDeleteConfirmVisible,
    show: showDeleteConfirm,
    hide: hideDeleteConfirm,
  } = useToggleVisibility();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);

  // Drop the marker once the flash has played, so the class can't replay on a
  // later re-render of that row.
  useEffect(() => {
    if (!highlightedRowId) return;
    const timer = setTimeout(() => setHighlightedRowId(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightedRowId]);

  const dataSource = useMemo(
    () =>
      Array.isArray(field.value)
        ? field.value.filter((item: FormArrayTableRecord) => !item.toBeDeleted)
        : [],
    [field.value]
  );

  const { sort, filters, sortedData, handleTableChange } =
    useLocalTableView<FormArrayTableRecord>(dataSource);

  // A detached copy: the row form must not write its field defaults back into
  // this table's live value just by opening.
  const editingRecord = useMemo(
    () => (editingIndex !== null ? cloneDeep(dataSource[editingIndex]) : {}),
    [editingIndex, dataSource]
  );

  const tableColumns = useMemo(
    () =>
      applyColumnFilters(
        buildTableColumns<FormArrayTableRecord>(
          propsColumns,
          withChildSchemaForm,
          context,
          rowItemName,
          sort
        ),
        dataSource,
        filters
      ),
    [
      propsColumns,
      withChildSchemaForm,
      context,
      rowItemName,
      sort,
      dataSource,
      filters,
    ]
  );

  const visibleRows = useMemo(
    () => filterRows(sortedData, tableColumns, filters),
    [sortedData, tableColumns, filters]
  );

  const {
    handleAdd,
    handleCancel,
    handleSubmit,
    handleConfirmDelete,
    handleCancelDelete,
    rowActionsCallback,
  } = useFormArrayTableActions({
    dataSource,
    field,
    canEditRow,
    canDeleteRow,
    canDuplicateRow,
    canEditRowConfig,
    canDeleteRowConfig,
    canDuplicateRowConfig,
    setEditingIndex,
    setDeletingIndex,
    setHighlightedRowId,
    show,
    showDeleteConfirm,
    hide,
    hideDeleteConfirm,
    editingIndex,
    pageData,
    disabled,
    rowItemName,
  });

  const rowClassName = useCallback(
    (record: FormArrayTableRecord) =>
      record.id === highlightedRowId ? 'row-added' : '',
    [highlightedRowId]
  );

  const rowKey = useCallback((record: FormArrayTableRecord, index?: number) => {
    if (record?.id) return record.id as string;
    console.error('FormArrayTable: Record missing id', record);
    return `fallback-${index}`;
  }, []);

  const { expandable } = useExpandableNestedTables(withChildSchemaForm);

  const shouldHideTable = hideEmpty && dataSource.length === 0;

  return (
    <Wrapper>
      {(title || (buttonVisible && !disabled)) && (
        <TableHeader>
          {<h4 style={{ margin: 0 }}>{title}</h4>}
          {buttonVisible && !disabled && (
            <Button
              type={buttonType}
              size={buttonSize}
              block={buttonBlock}
              danger={buttonDanger}
              disabled={buttonDisabled}
              ghost={buttonGhost}
              onClick={handleAdd}
            >
              {buttonText}
            </Button>
          )}
        </TableHeader>
      )}
      {!shouldHideTable && (
        <LibraryTable
          data={visibleRows}
          columns={tableColumns}
          rowKey={rowKey}
          rowClassName={rowClassName}
          rowActionsCallback={rowActionsCallback}
          expandable={expandable}
          onTableChange={handleTableChange}
        />
      )}
      {withChildSchemaForm && (
        <Modal
          open={isVisible}
          onCancel={handleCancel}
          footer={null}
          title={modalTitle || buttonText || ''}
          destroyOnHidden
          maskClosable={false}
          width={modelWidth || 720}
          styles={{
            content: { padding: 0 },
            header: { padding: 24, paddingBottom: 0 },
          }}
        >
          <AddRowForm
            maxHeight={modelContentMaxHeight}
            schema={withChildSchemaForm}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            initialValues={editingRecord}
            parentForm={field.form}
            context={context}
            cancelButtonText={cancelButtonText}
            saveButtonText={saveButtonText}
            disabled={disabled}
            rowItemName={rowItemName}
          />
        </Modal>
      )}
      <ConfirmModal
        isVisible={isDeleteConfirmVisible}
        title="Delete Row"
        onConfirm={handleConfirmDelete(deletingIndex)}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure you want to delete this row?</p>
      </ConfirmModal>
    </Wrapper>
  );
});

const HIGHLIGHT_MS = 1000;

const rowAdded = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  --row-added-bg: ${({ theme }) => theme.colors.primaryLight};

  .row-added > td {
    animation: ${rowAdded} ${HIGHLIGHT_MS}ms ease-out;
  }
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export default FormArrayTable;
